<template>
  <div class="knowledge-base">
    <!-- 头部：标题、搜索、刷新 -->
    <div class="header">
      <div class="title-wrap">
        <h2>{{ knowledgeBaseTitle }}</h2>
        <span class="subtitle">筛选、统计与排行</span>
      </div>
      <div class="actions">
        <el-input
          v-if="activeTab === 'list'"
          v-model="keyword"
          size="mini"
          clearable
          placeholder="按编号/知识点搜索"
          class="mr-8"
          @keyup.enter.native="fetchData"
        />
        <el-button v-if="activeTab === 'list'" size="mini" icon="el-icon-search" @click="fetchData">搜索</el-button>
        <el-button v-if="activeTab === 'list'" size="mini" type="primary" icon="el-icon-refresh" @click="fetchData" :loading="loading">刷新</el-button>
        <div style="width:12px"></div>
        <el-button type="text" @click="goBackToAMCCenter">
          <i class="el-icon-arrow-left" style="margin-right:4px"></i>返回备考中心
        </el-button>
      </div>
    </div>

    <el-alert v-if="error" type="error" :closable="false" :title="error" class="mb-12" />

    <!-- 标签页切换 -->
    <el-tabs v-model="activeTab" type="card" class="knowledge-tabs">
      <el-tab-pane label="知识点列表" name="list">
        <!-- 列表表格：选择、知识点（含错题数气泡）、点做（进度+入口）、升级数、70/80/90、近5/近10 -->
        <el-table
      ref="tbl"
      v-loading="loading"
      :data="displayData"
      border
      stripe
      size="small"
      height="calc(100vh - 230px)"
      :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
      row-key="id"
      :tree-props="{ children: 'children' }"
      @row-click="onRowClick"
      @selection-change="onSelectionChange"
    >
      <!-- 选择列 -->
      <el-table-column type="selection" width="48" />

      <!-- 知识点（hover 展示错题数） -->
      <el-table-column label="知识点" min-width="320" prop="content" class-name="col-knowledge">
        <template v-slot="{ row }">
          <el-popover placement="top-start" trigger="hover">
            <div class="popover">
              <div class="popover-title">错题数</div>
              <div class="wrong-count">{{ row.wrongCount }}</div>
            </div>
            <div slot="reference" class="k-content">
              <div class="k-text">{{ row.content }}</div>
              <el-tag v-if="row.wrongCount > 0" size="mini" type="danger">错题 {{ row.wrongCount }}</el-tag>
            </div>
          </el-popover>
        </template>
      </el-table-column>

      <!-- 总览（知识点题量） -->
      <el-table-column label="总览" width="140" prop="totalCount" :sortable="true">
        <template v-slot="{ row }">
          <span class="qty">{{ row.totalCount }} 题</span>
        </template>
      </el-table-column>

      <!-- 近5 / 近10 -->
      <el-table-column label="近5" width="90" prop="recent5" :sortable="true">
        <template v-slot="{ row }">
          <span>{{ formatPct(row.recent5) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="近10" width="90" prop="recent10" :sortable="true">
        <template v-slot="{ row }">
          <span>{{ formatPct(row.recent10) }}</span>
        </template>
      </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane v-if="showFormulaTab" label="公式定理" name="formulas">
        <div class="formula-page">
          <!-- 左侧导航栏 -->
          <div class="formula-nav">
            <div class="nav-search">
              <el-input
                v-model="formulaSearch"
                size="small"
                placeholder="搜索公式..."
                prefix-icon="el-icon-search"
                clearable
                @input="onFormulaSearch"
              />
            </div>
            <div class="nav-list">
              <div class="nav-category">
                <div class="nav-category-title" @click="scrollToSection('algebra')">代数 Algebra</div>
                <div class="nav-item" v-for="(item, key) in navItems.algebra" :key="'nav-a-'+key" @click="scrollToSection('algebra-'+key)">
                  {{ item }}
                </div>
              </div>
              <div class="nav-category">
                <div class="nav-category-title" @click="scrollToSection('geometry')">几何 Geometry</div>
                <div class="nav-item" v-for="(item, key) in navItems.geometry" :key="'nav-g-'+key" @click="scrollToSection('geometry-'+key)">
                  {{ item }}
                </div>
              </div>
              <div class="nav-category">
                <div class="nav-category-title" @click="scrollToSection('counting')">计数与概率</div>
                <div class="nav-item" v-for="(item, key) in navItems.counting" :key="'nav-c-'+key" @click="scrollToSection('counting-'+key)">
                  {{ item }}
                </div>
              </div>
              <div class="nav-category">
                <div class="nav-category-title" @click="scrollToSection('numbertheory')">数论</div>
                <div class="nav-item" v-for="(item, key) in navItems.numberTheory" :key="'nav-n-'+key" @click="scrollToSection('numbertheory-'+key)">
                  {{ item }}
                </div>
              </div>
            </div>
          </div>

          <!-- 右侧内容区 -->
          <div class="formula-container" ref="formulaContainer">
            <h3 class="formula-category-title" id="algebra">代数 Algebra</h3>

          <!-- 1. 实数运算 -->
          <div class="formula-section" id="algebra-realNumber">
            <div class="formula-header">
              <span class="formula-number">1</span>
              <div class="formula-title">
                <div class="formula-title-cn">实数运算</div>
                <div class="formula-title-en">Real Number Operations</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in algebraFormulas.realNumber" :key="'rn'+i" @click="onFormulaClick($event, f, '实数运算')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 2. 分数运算 -->
          <div class="formula-section" id="algebra-fractions">
            <div class="formula-header">
              <span class="formula-number">2</span>
              <div class="formula-title">
                <div class="formula-title-cn">分数运算</div>
                <div class="formula-title-en">Fractions and Decimals</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in algebraFormulas.fractions" :key="'fr'+i" @click="onFormulaClick($event, f, '分数运算')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 3. 幂和根号 -->
          <div class="formula-section" id="algebra-exponentials">
            <div class="formula-header">
              <span class="formula-number">3</span>
              <div class="formula-title">
                <div class="formula-title-cn">幂和根号</div>
                <div class="formula-title-en">Exponentials</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in algebraFormulas.exponentials" :key="'exp'+i" @click="onFormulaClick($event, f, '幂和根号')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 4. 根式运算 -->
          <div class="formula-section" id="algebra-radicals">
            <div class="formula-header">
              <span class="formula-number">4</span>
              <div class="formula-title">
                <div class="formula-title-cn">根式运算</div>
                <div class="formula-title-en">Radical Expressions</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in algebraFormulas.radicals" :key="'rad'+i" @click="onFormulaClick($event, f, '根式运算')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 5. 多项式运算 -->
          <div class="formula-section" id="algebra-polynomials">
            <div class="formula-header">
              <span class="formula-number">5</span>
              <div class="formula-title">
                <div class="formula-title-cn">多项式运算</div>
                <div class="formula-title-en">Polynomial Operations</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in algebraFormulas.polynomials" :key="'poly'+i" @click="onFormulaClick($event, f, '多项式运算')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 6. 等差数列 -->
          <div class="formula-section" id="algebra-arithmetic">
            <div class="formula-header">
              <span class="formula-number">6</span>
              <div class="formula-title">
                <div class="formula-title-cn">等差数列</div>
                <div class="formula-title-en">Arithmetic Sequences</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in algebraFormulas.arithmetic" :key="'arith'+i" @click="onFormulaClick($event, f, '等差数列')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 7. 等差级数 -->
          <div class="formula-section" id="algebra-series">
            <div class="formula-header">
              <span class="formula-number">7</span>
              <div class="formula-title">
                <div class="formula-title-cn">等差级数</div>
                <div class="formula-title-en">Arithmetic Series</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in algebraFormulas.series" :key="'ser'+i" @click="onFormulaClick($event, f, '等差级数')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 8. 等比数列 -->
          <div class="formula-section" id="algebra-geometric">
            <div class="formula-header">
              <span class="formula-number">8</span>
              <div class="formula-title">
                <div class="formula-title-cn">等比数列</div>
                <div class="formula-title-en">Geometric Sequences</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in algebraFormulas.geometric" :key="'geo'+i" @click="onFormulaClick($event, f, '等比数列')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 9. 公约数和公倍数 -->
          <div class="formula-section" id="algebra-divisors">
            <div class="formula-header">
              <span class="formula-number">9</span>
              <div class="formula-title">
                <div class="formula-title-cn">公约数和公倍数</div>
                <div class="formula-title-en">Common Divisors and Multiples</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in algebraFormulas.divisors" :key="'div'+i" @click="onFormulaClick($event, f, '公约数和公倍数')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 几何部分 -->
          <h3 class="formula-category-title" style="margin-top: 32px;" id="geometry">几何 Geometry</h3>

          <!-- 1. 角和线 -->
          <div class="formula-section" id="geometry-anglesLines">
            <div class="formula-header">
              <span class="formula-number">1</span>
              <div class="formula-title">
                <div class="formula-title-cn">角和线</div>
                <div class="formula-title-en">Angles and Lines</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in geometryFormulas.anglesLines" :key="'al'+i" @click="onFormulaClick($event, f, '角和线')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 2. 三角形性质 -->
          <div class="formula-section" id="geometry-triangleProps">
            <div class="formula-header">
              <span class="formula-number">2</span>
              <div class="formula-title">
                <div class="formula-title-cn">三角形性质</div>
                <div class="formula-title-en">Properties of Triangles</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in geometryFormulas.triangleProps" :key="'tp'+i" @click="onFormulaClick($event, f, '三角形性质')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 3. 三角形五心 -->
          <div class="formula-section" id="geometry-triangleCenters">
            <div class="formula-header">
              <span class="formula-number">3</span>
              <div class="formula-title">
                <div class="formula-title-cn">三角形五心</div>
                <div class="formula-title-en">Five Centers of Triangle</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in geometryFormulas.triangleCenters" :key="'tc'+i" @click="onFormulaClick($event, f, '三角形五心')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 4. 特殊三角形 -->
          <div class="formula-section" id="geometry-specialTriangles">
            <div class="formula-header">
              <span class="formula-number">4</span>
              <div class="formula-title">
                <div class="formula-title-cn">特殊三角形</div>
                <div class="formula-title-en">Special Triangles</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in geometryFormulas.specialTriangles" :key="'st'+i" @click="onFormulaClick($event, f, '特殊三角形')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 5. 相似三角形 -->
          <div class="formula-section" id="geometry-similarTriangles">
            <div class="formula-header">
              <span class="formula-number">5</span>
              <div class="formula-title">
                <div class="formula-title-cn">相似三角形</div>
                <div class="formula-title-en">Similar Triangles</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in geometryFormulas.similarTriangles" :key="'sim'+i" @click="onFormulaClick($event, f, '相似三角形')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 6. 面积比例 -->
          <div class="formula-section" id="geometry-areaRatios">
            <div class="formula-header">
              <span class="formula-number">6</span>
              <div class="formula-title">
                <div class="formula-title-cn">面积比例</div>
                <div class="formula-title-en">Area Ratios</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in geometryFormulas.areaRatios" :key="'ar'+i" @click="onFormulaClick($event, f, '面积比例')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 7. 多边形 -->
          <div class="formula-section" id="geometry-polygons">
            <div class="formula-header">
              <span class="formula-number">7</span>
              <div class="formula-title">
                <div class="formula-title-cn">多边形</div>
                <div class="formula-title-en">Polygons</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in geometryFormulas.polygons" :key="'pg'+i" @click="onFormulaClick($event, f, '多边形')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 8. 圆 -->
          <div class="formula-section" id="geometry-circles">
            <div class="formula-header">
              <span class="formula-number">8</span>
              <div class="formula-title">
                <div class="formula-title-cn">圆</div>
                <div class="formula-title-en">Circles</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in geometryFormulas.circles" :key="'cir'+i" @click="onFormulaClick($event, f, '圆')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 9. 解析几何 -->
          <div class="formula-section" id="geometry-analytical">
            <div class="formula-header">
              <span class="formula-number">9</span>
              <div class="formula-title">
                <div class="formula-title-cn">解析几何</div>
                <div class="formula-title-en">Analytical Geometry</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in geometryFormulas.analytical" :key="'ana'+i" @click="onFormulaClick($event, f, '解析几何')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 10. 立体几何 -->
          <div class="formula-section" id="geometry-solid">
            <div class="formula-header">
              <span class="formula-number">10</span>
              <div class="formula-title">
                <div class="formula-title-cn">立体几何</div>
                <div class="formula-title-en">Solid Geometry</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in geometryFormulas.solid" :key="'sol'+i" @click="onFormulaClick($event, f, '立体几何')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 计数与概率部分 -->
          <h3 class="formula-category-title" style="margin-top: 32px;" id="counting">计数与概率 Counting and Probability</h3>

          <!-- 1. 加法原理 -->
          <div class="formula-section" id="counting-sumRule">
            <div class="formula-header">
              <span class="formula-number">1</span>
              <div class="formula-title">
                <div class="formula-title-cn">加法原理</div>
                <div class="formula-title-en">Sum Rule (Addition Principle)</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in countingFormulas.sumRule" :key="'sum'+i" @click="onFormulaClick($event, f, '加法原理')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 2. 乘法原理 -->
          <div class="formula-section" id="counting-productRule">
            <div class="formula-header">
              <span class="formula-number">2</span>
              <div class="formula-title">
                <div class="formula-title-cn">乘法原理</div>
                <div class="formula-title-en">Product Rule (Multiplication Principle)</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in countingFormulas.productRule" :key="'prod'+i" @click="onFormulaClick($event, f, '乘法原理')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 3. 概率的主要性质 -->
          <div class="formula-section" id="counting-probability">
            <div class="formula-header">
              <span class="formula-number">3</span>
              <div class="formula-title">
                <div class="formula-title-cn">概率的主要性质</div>
                <div class="formula-title-en">Properties of Probability</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in countingFormulas.probability" :key="'prob'+i" @click="onFormulaClick($event, f, '概率性质')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 4. 排列组合 -->
          <div class="formula-section" id="counting-permComb">
            <div class="formula-header">
              <span class="formula-number">4</span>
              <div class="formula-title">
                <div class="formula-title-cn">排列组合</div>
                <div class="formula-title-en">Permutation and Combination</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in countingFormulas.permComb" :key="'pc'+i" @click="onFormulaClick($event, f, '排列组合')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 5. 概率基本公式 -->
          <div class="formula-section" id="counting-basicProb">
            <div class="formula-header">
              <span class="formula-number">5</span>
              <div class="formula-title">
                <div class="formula-title-cn">概率基本公式</div>
                <div class="formula-title-en">Basic Probability Formulas</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in countingFormulas.basicProb" :key="'bp'+i" @click="onFormulaClick($event, f, '概率基本公式')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 数论部分 -->
          <h3 class="formula-category-title" style="margin-top: 32px;" id="numbertheory">数论 Number Theory</h3>

          <!-- 1. 奇偶性运算 -->
          <div class="formula-section" id="numbertheory-evenOdd">
            <div class="formula-header">
              <span class="formula-number">1</span>
              <div class="formula-title">
                <div class="formula-title-cn">奇偶性运算</div>
                <div class="formula-title-en">Even and Odd Operations</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in numberTheoryFormulas.evenOdd" :key="'eo'+i" @click="onFormulaClick($event, f, '奇偶性运算')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 2. 连续整数性质 -->
          <div class="formula-section" id="numbertheory-consecutive">
            <div class="formula-header">
              <span class="formula-number">2</span>
              <div class="formula-title">
                <div class="formula-title-cn">连续整数性质</div>
                <div class="formula-title-en">Consecutive Integers Properties</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in numberTheoryFormulas.consecutive" :key="'cons'+i" @click="onFormulaClick($event, f, '连续整数性质')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 3. 奇偶性判定 -->
          <div class="formula-section" id="numbertheory-parityRules">
            <div class="formula-header">
              <span class="formula-number">3</span>
              <div class="formula-title">
                <div class="formula-title-cn">奇偶性判定</div>
                <div class="formula-title-en">Parity Rules</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in numberTheoryFormulas.parityRules" :key="'pr'+i" @click="onFormulaClick($event, f, '奇偶性判定')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          <!-- 4. 整除性判定 -->
          <div class="formula-section" id="numbertheory-divisibility">
            <div class="formula-header">
              <span class="formula-number">4</span>
              <div class="formula-title">
                <div class="formula-title-cn">整除性判定</div>
                <div class="formula-title-en">Divisibility Rules</div>
              </div>
            </div>
            <div class="formula-content">
              <div class="formula-item clickable" v-for="(f, i) in numberTheoryFormulas.divisibility" :key="'dv'+i" @click="onFormulaClick($event, f, '整除性判定')" v-html="renderFormula(f)"></div>
            </div>
          </div>

          </div>
        </div>
      </el-tab-pane>
    </el-tabs>

    <!-- 公式操作菜单 -->
    <div
      v-if="formulaMenuVisible"
      class="formula-menu"
      :style="{ left: formulaMenuPosition.x + 'px', top: formulaMenuPosition.y + 'px' }"
    >
      <div class="formula-menu-item" @click="zoomFormula">
        <i class="el-icon-zoom-in"></i>
        <span>放大查看</span>
      </div>
      <div class="formula-menu-item" @click="askAiExplain">
        <i class="el-icon-chat-dot-round"></i>
        <span>讲解公式</span>
      </div>
      <div class="formula-menu-item" @click="askAiPractice">
        <i class="el-icon-edit-outline"></i>
        <span>生成练习题</span>
      </div>
    </div>

    <!-- 公式放大对话框 -->
    <el-dialog
      :visible.sync="zoomDialogVisible"
      :title="'公式详情 - ' + currentFormulaCategory"
      width="600px"
      center
      custom-class="formula-zoom-dialog"
    >
      <div class="zoom-formula-content" v-html="renderFormulaLarge(currentFormula)"></div>
      <div class="zoom-actions">
        <el-button type="primary" size="small" @click="askAiExplainFromZoom">
          <i class="el-icon-chat-dot-round"></i> 讲解公式
        </el-button>
        <el-button type="success" size="small" @click="askAiPracticeFromZoom">
          <i class="el-icon-edit-outline"></i> 生成练习题
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listGeneralKnowledgeTree, getGeneralKnowledgeStats, getKnowledgeProgressByRepoId } from '@/api/knowledge'
import katex from 'katex'
import 'katex/dist/katex.min.css'

export default {
  name: 'KnowledgeBase',
  data() {
    return {
      loading: false,
      error: '',
      list: [],
      keyword: '',
      repoId: null,
      questions: [],
      activeTab: 'list',
      formulaSearch: '',
      // 公式交互相关
      formulaMenuVisible: false,
      formulaMenuPosition: { x: 0, y: 0 },
      currentFormula: '',
      currentFormulaCategory: '',
      zoomDialogVisible: false,
      navItems: {
        algebra: {
          realNumber: '1. 实数运算',
          fractions: '2. 分数运算',
          exponentials: '3. 幂和根号',
          radicals: '4. 根式运算',
          polynomials: '5. 多项式运算',
          arithmetic: '6. 等差数列',
          series: '7. 等差级数',
          geometric: '8. 等比数列',
          divisors: '9. 公约数公倍数'
        },
        geometry: {
          anglesLines: '1. 角和线',
          triangleProps: '2. 三角形性质',
          triangleCenters: '3. 三角形五心',
          specialTriangles: '4. 特殊三角形',
          similarTriangles: '5. 相似三角形',
          areaRatios: '6. 面积比例',
          polygons: '7. 多边形',
          circles: '8. 圆',
          analytical: '9. 解析几何',
          solid: '10. 立体几何'
        },
        counting: {
          sumRule: '1. 加法原理',
          productRule: '2. 乘法原理',
          probability: '3. 概率性质',
          permComb: '4. 排列组合',
          basicProb: '5. 概率公式'
        },
        numberTheory: {
          evenOdd: '1. 奇偶性运算',
          consecutive: '2. 连续整数',
          parityRules: '3. 奇偶性判定',
          divisibility: '4. 整除性判定'
        }
      },
      algebraFormulas: {
        realNumber: [
          'a + b = b + a',
          'a \\cdot b = b \\cdot a',
          '(a + b) + c = a + (b + c)',
          '(a \\cdot b) \\cdot c = a \\cdot (b \\cdot c)',
          'a \\cdot (b + c) = a \\cdot b + a \\cdot c',
          'a + 0 = a',
          'a \\cdot 1 = a',
          'a + (-a) = 0',
          'a \\cdot \\frac{1}{a} = 1 \\quad (a \\neq 0)'
        ],
        fractions: [
          '\\frac{a}{b} + \\frac{c}{d} = \\frac{ad + bc}{bd}',
          '\\frac{a}{b} - \\frac{c}{d} = \\frac{ad - bc}{bd}',
          '\\frac{a}{b} \\cdot \\frac{c}{d} = \\frac{ac}{bd}',
          '\\frac{a}{b} \\div \\frac{c}{d} = \\frac{a}{b} \\cdot \\frac{d}{c} = \\frac{ad}{bc}',
          '\\frac{a}{b} = \\frac{ac}{bc} \\quad (c \\neq 0)'
        ],
        exponentials: [
          'a^m \\cdot a^n = a^{m+n}',
          '\\frac{a^m}{a^n} = a^{m-n}',
          '(a^m)^n = a^{mn}',
          '(ab)^n = a^n b^n',
          'a^0 = 1 \\quad (a \\neq 0)',
          'a^{-n} = \\frac{1}{a^n}'
        ],
        radicals: [
          '\\sqrt[n]{a} = a^{\\frac{1}{n}}',
          '\\sqrt[n]{ab} = \\sqrt[n]{a} \\cdot \\sqrt[n]{b}',
          '\\sqrt[n]{\\frac{a}{b}} = \\frac{\\sqrt[n]{a}}{\\sqrt[n]{b}}',
          '\\sqrt[m]{\\sqrt[n]{a}} = \\sqrt[mn]{a}',
          '(\\sqrt[n]{a})^m = \\sqrt[n]{a^m}'
        ],
        polynomials: [
          '(a + b)^2 = a^2 + 2ab + b^2',
          '(a - b)^2 = a^2 - 2ab + b^2',
          'a^2 - b^2 = (a + b)(a - b)',
          '(a + b)^3 = a^3 + 3a^2b + 3ab^2 + b^3',
          '(a - b)^3 = a^3 - 3a^2b + 3ab^2 - b^3',
          'a^3 + b^3 = (a + b)(a^2 - ab + b^2)',
          'a^3 - b^3 = (a - b)(a^2 + ab + b^2)'
        ],
        arithmetic: [
          'a_n = a_1 + (n-1)d',
          'a_n = a_m + (n-m)d',
          'd = a_{n+1} - a_n'
        ],
        series: [
          'S_n = \\frac{n(a_1 + a_n)}{2}',
          'S_n = \\frac{n[2a_1 + (n-1)d]}{2}',
          '1 + 2 + 3 + \\cdots + n = \\frac{n(n+1)}{2}'
        ],
        geometric: [
          'a_n = a_1 \\cdot r^{n-1}',
          'S_n = \\frac{a_1(1 - r^n)}{1 - r} \\quad (r \\neq 1)',
          'S_n = \\frac{a_1 - a_n r}{1 - r} \\quad (r \\neq 1)',
          'S_\\infty = \\frac{a_1}{1 - r} \\quad (|r| < 1)'
        ],
        divisors: [
          '\\gcd(a, b) \\cdot \\text{lcm}(a, b) = a \\cdot b',
          '\\gcd(a, b) = \\gcd(b, a \\mod b)',
          '1^2 = 1',
          '2^2 = 4',
          '3^2 = 9',
          '4^2 = 16',
          '5^2 = 25',
          '6^2 = 36',
          '7^2 = 49',
          '8^2 = 64',
          '9^2 = 81',
          '10^2 = 100',
          '11^2 = 121',
          '12^2 = 144',
          '13^2 = 169',
          '14^2 = 196',
          '15^2 = 225',
          '16^2 = 256',
          '17^2 = 289',
          '18^2 = 324',
          '19^2 = 361',
          '20^2 = 400'
        ]
      },
      geometryFormulas: {
        anglesLines: [
          '\\text{互补角: } a + b = 90°',
          '\\text{补角: } a + b = 180°',
          '\\text{内错角相等: } \\angle 1 = \\angle 3, \\angle 4 = \\angle 5',
          '\\text{外错角相等: } \\angle 1 = \\angle 8, \\angle 2 = \\angle 7',
          '\\text{同位角相等: } \\angle 1 = \\angle 5, \\angle 2 = \\angle 6, \\angle 3 = \\angle 7, \\angle 4 = \\angle 8',
          '\\text{同旁内角互补: } \\angle 3 + \\angle 5 = 180°, \\angle 4 + \\angle 6 = 180°'
        ],
        triangleProps: [
          'S_{\\triangle} = \\frac{1}{2} \\times \\text{base} \\times \\text{height}',
          '\\text{两边之和大于第三边}',
          '\\text{两边之差小于第三边}',
          '\\text{勾股定理: } a^2 + b^2 = c^2',
          '3^2 + 4^2 = 5^2',
          '5^2 + 12^2 = 13^2',
          '7^2 + 24^2 = 25^2',
          '8^2 + 15^2 = 17^2'
        ],
        triangleCenters: [
          '\\text{中线: } BD = DC',
          '\\text{角平分线: } \\angle BAD = \\angle CAD, DE = DF',
          '\\text{高线: } AD \\perp BC, S = \\frac{1}{2} \\times AD \\times BC',
          '\\text{垂直平分线: } BD = DC, DE \\perp BC, FB = FC',
          '\\text{重心: } AG = 2GD, BG = 2GE, CG = 2GF',
          '\\text{中位线: } DE = \\frac{1}{2}BC, DE \\parallel BC'
        ],
        specialTriangles: [
          '\\text{等腰三角形: 两腰相等, 两底角相等}',
          '\\text{等腰三角形: } \\angle A = \\angle C, AB = BC',
          '\\text{等边三角形: 三边相等, 三角都是 } 60°',
          '\\text{等边三角形: } AB = BC = CA, \\angle A = \\angle B = \\angle C = 60°'
        ],
        similarTriangles: [
          '\\text{判定 SSS: 三边成比例}',
          '\\text{判定 SAS: 两边成比例且夹角相等}',
          '\\text{判定 AA: 两角对应相等}',
          '\\frac{AB}{DE} = \\frac{BC}{EF} = \\frac{CA}{FD}',
          '\\triangle ABC \\sim \\triangle DEF'
        ],
        areaRatios: [
          '\\text{相似三角形面积比} = \\text{相似比}^2',
          '\\frac{S_{\\triangle ABC}}{S_{\\triangle DEF}} = \\left(\\frac{AB}{DE}\\right)^2',
          '\\text{等高三角形面积比} = \\text{底边比}',
          '\\text{等底三角形面积比} = \\text{高之比}'
        ],
        polygons: [
          '\\text{内角和: } (n-2) \\times 180°',
          '\\text{外角和: } 360°',
          '\\text{正多边形每个内角: } \\frac{(n-2) \\times 180°}{n}',
          '\\text{正多边形每个外角: } \\frac{360°}{n}',
          '\\text{对角线数量: } \\frac{n(n-3)}{2}',
          '\\text{从一个顶点出发的对角线: } n - 3'
        ],
        circles: [
          '\\text{周长: } C = \\pi d = 2\\pi r',
          '\\text{面积: } A = \\pi r^2 = \\frac{1}{4}\\pi d^2',
          '\\text{圆心角 = 2 × 圆周角}',
          '\\text{同弧所对的圆周角相等}',
          '\\text{扇形面积: } S = \\frac{\\theta}{360°} \\times \\pi r^2',
          '\\text{扇形弧长: } l = \\frac{\\theta}{360°} \\times 2\\pi r'
        ],
        analytical: [
          '\\text{两点间距离: } d = \\sqrt{(x_2-x_1)^2 + (y_2-y_1)^2}',
          '\\text{中点坐标: } M = \\left(\\frac{x_1+x_2}{2}, \\frac{y_1+y_2}{2}\\right)',
          '\\text{斜率: } k = \\frac{y_2-y_1}{x_2-x_1}'
        ],
        solid: [
          '\\text{欧拉公式: } V - E + F = 2',
          '\\text{正方体表面积: } 6a^2',
          '\\text{正方体体积: } a^3',
          '\\text{长方体表面积: } 2(ab + bc + ca)',
          '\\text{长方体体积: } abc',
          '\\text{棱柱体积: } S_{底} \\times h',
          '\\text{圆柱表面积: } 2\\pi rh + 2\\pi r^2 = 2\\pi r(h+r)',
          '\\text{圆柱体积: } \\pi r^2 h',
          '\\text{棱锥体积: } \\frac{1}{3} S_{底} \\times h',
          '\\text{圆锥表面积: } \\pi rl + \\pi r^2 = \\pi r(l+r)',
          '\\text{圆锥体积: } \\frac{1}{3}\\pi r^2 h',
          '\\text{球表面积: } 4\\pi r^2',
          '\\text{球体积: } \\frac{4}{3}\\pi r^3'
        ]
      },
      countingFormulas: {
        sumRule: [
          '\\text{如果事件 } E_1 \\text{ 有 } n_1 \\text{ 种方式, } E_2 \\text{ 有 } n_2 \\text{ 种方式...}',
          '\\text{总方式数: } n_1 + n_2 + \\cdots + n_k'
        ],
        productRule: [
          '\\text{如果任务分 } k \\text{ 步完成}',
          '\\text{第一步 } n_1 \\text{ 种, 第二步 } n_2 \\text{ 种...}',
          '\\text{总方式数: } n_1 \\times n_2 \\times \\cdots \\times n_k'
        ],
        probability: [
          '0 \\leq P(E) \\leq 1',
          'P(\\text{不可能事件}) = 0',
          'P(\\text{必然事件}) = 1',
          'P(E) + P(\\overline{E}) = 1'
        ],
        permComb: [
          'n! = n \\times (n-1) \\times \\cdots \\times 3 \\times 2 \\times 1',
          '\\text{排列: } _nP_r = \\frac{n!}{(n-r)!}',
          '\\text{组合: } _nC_r = \\frac{n!}{r!(n-r)!}',
          '\\text{重复排列: } \\frac{n!}{p! \\times q! \\times r! \\times \\cdots}',
          '_nC_0 + _nC_1 + \\cdots + _nC_n = 2^n'
        ],
        basicProb: [
          'P = \\frac{\\text{有利结果数}}{\\text{所有可能结果数}}',
          'n(A \\cup B) = n(A) + n(B) - n(A \\cap B)',
          'n(A \\cup B \\cup C) = n(A) + n(B) + n(C)',
          '\\quad - n(A \\cap B) - n(B \\cap C) - n(A \\cap C) + n(A \\cap B \\cap C)'
        ]
      },
      numberTheoryFormulas: {
        evenOdd: [
          '\\text{偶} \\pm \\text{偶} = \\text{偶}',
          '\\text{奇} \\pm \\text{奇} = \\text{偶}',
          '\\text{偶} \\pm \\text{奇} = \\text{奇}',
          '\\text{偶} \\times \\text{偶} = \\text{偶}',
          '\\text{奇} \\times \\text{奇} = \\text{奇}',
          '\\text{偶} \\times \\text{奇} = \\text{偶}',
          '\\text{偶} \\div \\text{偶} = \\text{偶/奇/分数}',
          '\\text{偶} \\div \\text{奇} = \\text{偶/分数}',
          '\\text{奇} \\div \\text{奇} = \\text{奇/分数}'
        ],
        consecutive: [
          '\\text{两个连续整数的和是奇数: } n + (n+1) = 2n + 1',
          '\\text{两个连续整数的乘积是偶数: } n \\times (n+1)',
          '\\text{两个连续整数奇偶性相反（一奇一偶）}'
        ],
        parityRules: [
          '\\text{n个正整数乘积为偶数} \\Rightarrow \\text{至少一个是偶数}',
          '\\text{n个正整数乘积为奇数} \\Rightarrow \\text{全部是奇数}',
          '\\text{偶数个奇数的和是偶数}',
          '\\text{奇数个奇数的和是奇数}'
        ],
        divisibility: [
          '\\text{被2整除: 末位是偶数}',
          '\\text{被3整除: 各位数字之和能被3整除}',
          '\\text{被4整除: 末两位能被4整除}',
          '\\text{被5整除: 末位是0或5}',
          '\\text{被6整除: 同时能被2和3整除}',
          '\\text{被8整除: 末三位能被8整除}',
          '\\text{被9整除: 各位数字之和能被9整除}',
          '\\text{被11整除: 奇数位和与偶数位和之差能被11整除}'
        ]
      }
    }
  },
  created() {
    this.fetchData()
  },
  mounted() {
    // 进入页面：自动收起左侧导航栏（向左隐藏）
    try {
      this.$store && this.$store.dispatch && this.$store.dispatch('app/closeSideBar', { withoutAnimation: true })
    } catch (e) { /* no-op */ }
  },
  beforeDestroy() {
    // 离开页面：还原侧边栏状态
    try {
      this.$store && this.$store.dispatch && this.$store.dispatch('app/openSideBar', { withoutAnimation: true })
    } catch (e) { /* no-op */ }
    // 清除知识点上下文
    window.__currentKnowledgeContext = null
    try {
      localStorage.removeItem('currentKnowledgeContext')
    } catch (e) {}
  },
  methods: {
    filterEmptyKnowledgeNodes(nodes) {
      if (!Array.isArray(nodes)) return []
      return nodes.reduce((result, node) => {
        if (!node) return result
        const nextNode = { ...node }
        if (Array.isArray(nextNode.children) && nextNode.children.length > 0) {
          nextNode.children = this.filterEmptyKnowledgeNodes(nextNode.children)
        }
        const totalCount = Number(nextNode.totalCount || 0)
        const hasChildren = Array.isArray(nextNode.children) && nextNode.children.length > 0
        if (totalCount > 0 || hasChildren) {
          result.push(nextNode)
        }
        return result
      }, [])
    },

    async fetchData() {
      this.loading = true
      this.error = ''
      if (!this.showFormulaTab && this.activeTab === 'formulas') {
        this.activeTab = 'list'
      }
      try {
        // 读取题库ID
        this.repoId = this.$route.query?.repoId || this.repoId

        if (!this.repoId) {
          this.error = '缺少题库ID参数'
          this.loading = false
          return
        }

        // 1. 获取该题库完整知识点树（后端已组装好 children 字段）
        const res = await listGeneralKnowledgeTree(this.repoId)
        const treeData = Array.isArray(res?.data) ? res.data : (res?.data?.data || [])

        // 2. 获取知识点进度统计
        let progressPayload = {}
        try {
          const progressRes = await getKnowledgeProgressByRepoId(this.repoId)
          progressPayload = (progressRes && progressRes.data && progressRes.data.data) || progressRes?.data || progressRes || {}
        } catch (e) {
          console.warn('获取知识点进度失败:', e)
          progressPayload = {}
        }

        const progressList = Array.isArray(progressPayload?.knowledgeList) ? progressPayload.knowledgeList : []
        const byId = new Map(progressList.map(i => [i.id, i]))

        // 3. 递归将进度统计合并到树的每个节点（包括子节点）
        const mergeProgress = (nodes, idx = { val: 0 }) => {
          return nodes.map(k => {
            const progress = byId.get(k.id) || {}
            const children = Array.isArray(k.children) && k.children.length > 0
              ? mergeProgress(k.children, idx)
              : []
            idx.val++
            return {
              ...k,
              content: k.content || k.name || '',
              wrongCount: progress.wrongCount || 0,
              totalCount: progress.total || progress.totalCount || 0,
              recent5: progress.recent5 || 0,
              recent10: progress.recent10 || 0,
              children
            }
          })
        }

        this.list = this.filterEmptyKnowledgeNodes(mergeProgress(treeData))

        console.log('[KnowledgeBase] 加载知识点数据:', {
          repoId: this.repoId,
          total: this.list.length,
          sample: this.list[0]
        })
      } catch (e) {
        console.error('[KnowledgeBase] 加载失败:', e)
        this.error = e?.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    goBackToAMCCenter() {
      const routeQuery = this.$route?.query || {}
      const query = {
        ...(routeQuery.repoId != null && String(routeQuery.repoId).trim() !== '' ? { repoId: routeQuery.repoId } : {}),
        ...(routeQuery.level != null && String(routeQuery.level).trim() !== '' ? { level: routeQuery.level } : {}),
        ...(routeQuery.courseTypeId != null && String(routeQuery.courseTypeId).trim() !== '' ? { courseTypeId: routeQuery.courseTypeId } : {}),
        ...(routeQuery.courseTypeCode != null && String(routeQuery.courseTypeCode).trim() !== '' ? { courseTypeCode: routeQuery.courseTypeCode } : {})
      }
      this.$router.push({
        path: '/amc-course',
        query
      })
    },
    onRowClick(row) {
      if (!row || row.children) return
      const q = {
        // ...
      }
      // ...
    },
    // ...
    // 复选框选择变化时更新知识点上下文
    onSelectionChange(selection) {
      // 取最后选中的一个作为当前知识点
      if (selection && selection.length > 0) {
        const lastSelected = selection[selection.length - 1]
        this.setKnowledgeContext(lastSelected)
      }
    },
    // 设置知识点上下文
    setKnowledgeContext(row) {
      if (row && row.content) {
        const ctx = {
          type: 'knowledge',
          id: row.id,
          name: row.content,
          totalCount: row.totalCount,
          wrongCount: row.wrongCount
        }
        window.__currentKnowledgeContext = ctx
        try {
          localStorage.setItem('currentKnowledgeContext', JSON.stringify(ctx))
        } catch (e) {}
      }
    },
    formatPct(val) {
      const n = Number(val) || 0
      return `${Math.round(n)}%`
    },
    goPractice(row) {
      this.$router.push({
        path: '/knowledge-question-practice',
        query: { knowledge: row.content, repoId: this.repoId }
      })
    },
    renderFormula(latex) {
      try {
        return katex.renderToString(latex, {
          throwOnError: false,
          displayMode: false
        })
      } catch (e) {
        return latex
      }
    },
    scrollToSection(sectionId) {
      const el = document.getElementById(sectionId)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    onFormulaSearch() {
      const kw = (this.formulaSearch || '').trim().toLowerCase()
      if (!kw) return
      // 搜索所有公式section，找到第一个匹配的
      const sections = document.querySelectorAll('.formula-section')
      for (const sec of sections) {
        const text = sec.textContent.toLowerCase()
        if (text.includes(kw)) {
          sec.scrollIntoView({ behavior: 'smooth', block: 'start' })
          // 高亮效果
          sec.classList.add('highlight')
          setTimeout(() => sec.classList.remove('highlight'), 2000)
          break
        }
      }
    },
    // 公式点击事件
    onFormulaClick(event, formula, category) {
      event.stopPropagation()
      this.currentFormula = formula
      this.currentFormulaCategory = category
      // 计算菜单位置
      const rect = event.target.getBoundingClientRect()
      this.formulaMenuPosition = {
        x: rect.left,
        y: rect.bottom + 5
      }
      this.formulaMenuVisible = true
      // 点击其他地方关闭菜单
      setTimeout(() => {
        document.addEventListener('click', this.closeFormulaMenu, { once: true })
      }, 0)
    },
    closeFormulaMenu() {
      this.formulaMenuVisible = false
    },
    // 放大公式
    zoomFormula() {
      this.formulaMenuVisible = false
      this.zoomDialogVisible = true
    },
    // 渲染大号公式
    renderFormulaLarge(latex) {
      try {
        return katex.renderToString(latex, {
          throwOnError: false,
          displayMode: true
        })
      } catch (e) {
        return latex
      }
    },
    // 向AI求助 - 讲解公式
    askAiExplain() {
      this.formulaMenuVisible = false
      this.setFormulaContext('explain')
      this.openAiAssistant()
    },
    // 向AI求助 - 生成练习题
    askAiPractice() {
      this.formulaMenuVisible = false
      this.setFormulaContext('practice')
      this.openAiAssistant()
    },
    // 从放大对话框中讲解
    askAiExplainFromZoom() {
      this.zoomDialogVisible = false
      this.setFormulaContext('explain')
      this.openAiAssistant()
    },
    // 从放大对话框中生成练习题
    askAiPracticeFromZoom() {
      this.zoomDialogVisible = false
      this.setFormulaContext('practice')
      this.openAiAssistant()
    },
    // 设置公式上下文供AI助手使用
    setFormulaContext(actionType) {
      const ctx = {
        type: 'formula',
        formula: this.currentFormula,
        category: this.currentFormulaCategory,
        actionType: actionType // 'explain' 或 'practice'
      }
      window.__currentFormulaContext = ctx
      try {
        localStorage.setItem('currentFormulaContext', JSON.stringify(ctx))
      } catch (e) {}
    },
    // 打开AI助手
    openAiAssistant() {
      // 触发全局事件打开AI助手
      this.$nextTick(() => {
        // 尝试通过事件总线或直接调用打开AI助手
        if (window.__openAiAssistant) {
          window.__openAiAssistant()
        } else {
          // 备用方案：触发自定义事件
          window.dispatchEvent(new CustomEvent('openAiAssistant'))
        }
      })
    }
  },
  computed: {
    courseDisplayName() {
      const routeQuery = this.$route?.query || {}
      const displayNameCandidates = [
        routeQuery.courseName,
        routeQuery.selectedLevelDisplay,
        routeQuery.levelName,
        routeQuery.level,
        routeQuery.courseTypeName,
        routeQuery.courseTypeCode
      ]
      for (const item of displayNameCandidates) {
        const value = String(item || '').trim()
        if (value) {
          return value.toUpperCase()
        }
      }
      return '课程'
    },
    knowledgeBaseTitle() {
      return `${this.courseDisplayName} 知识库`
    },
    showFormulaTab() {
      const level = String(this.$route.query?.level || '').trim().toLowerCase()
      const courseTypeCode = String(this.$route.query?.courseTypeCode || '').trim().toLowerCase()
      return level.startsWith('amc') || courseTypeCode === 'amc'
    },
    // 后端已返回完整树结构（含 children），直接使用
    treeData() {
      return this.list || []
    },
    // 根据是否搜索决定显示树或扁平过滤
    displayData() {
      const kw = (this.keyword || '').trim().toLowerCase()
      if (!kw) return this.treeData
      return (this.list || []).filter(i =>
        String(i.id || '').toLowerCase().includes(kw) ||
        String(i.content || '').toLowerCase().includes(kw)
      )
    }
  }
}
</script>

<style scoped>
.knowledge-base {
  padding: 16px;
}
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.title-wrap {
  display: flex;
  align-items: baseline;
  gap: 10px;
}
.subtitle {
  font-size: 12px;
  color: #909399;
}
.actions { display: flex; align-items: center; }
.mr-8 { margin-right: 8px; }
.mb-12 { margin-bottom: 12px; }

.k-content { display: flex; align-items: center; gap: 8px; }
.k-text { max-width: 520px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.popover { min-width: 160px; }
.popover-title { font-weight: 600; margin-bottom: 6px; }
.wrong-count { color: #f56c6c; font-size: 16px; }

.do-col { display: flex; flex-direction: column; gap: 6px; }
.do-stats { font-size: 12px; color: #606266; }

/* 树形箭头与文本同排居中 */
::v-deep .col-knowledge .cell {
  display: flex;
  align-items: center;
  gap: 8px;
}
::v-deep .col-knowledge .el-popover__reference {
  display: inline-flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}
::v-deep .col-knowledge .el-table__expand-icon {
  margin-right: 4px;
}

/* 父子级层级视觉区分 */
::v-deep .el-table__row--level-0 .col-knowledge .k-text {
  font-weight: 600;
  color: #303133;
}
::v-deep .el-table__row--level-1 .col-knowledge .k-text {
  color: #606266;
}
::v-deep .el-table__row--level-2 .col-knowledge .k-text {
  color: #909399;
}
/* 辅助层级标识：细边线与内边距 */
::v-deep .el-table__row--level-1 .col-knowledge .k-content {
  padding-left: 4px;
  border-left: 2px solid #ebeef5;
}
::v-deep .el-table__row--level-2 .col-knowledge .k-content {
  padding-left: 8px;
  border-left: 2px dashed #ebeef5;
}

/* 标签页样式 */
.knowledge-tabs {
  margin-top: 8px;
}

/* 公式定理页面样式 */
.formula-container {
  padding: 20px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  background: #fafbfc;
  border-radius: 8px;
}

.formula-category-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a73e8;
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 2px solid #1a73e8;
}

.formula-section {
  background: #fff;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.formula-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.formula-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #1a73e8;
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  border-radius: 50%;
  flex-shrink: 0;
}

.formula-title {
  flex: 1;
}

.formula-title-cn {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.formula-title-en {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.formula-content {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
  padding-left: 40px;
}

.formula-item {
  font-size: 15px;
  color: #303133;
  padding: 6px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  border-left: 3px solid #1a73e8;
}

/* 公式页面布局 */
.formula-page {
  display: flex;
  gap: 16px;
  height: calc(100vh - 200px);
}

/* 左侧导航栏 */
.formula-nav {
  width: 200px;
  flex-shrink: 0;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.nav-search {
  padding: 12px;
  border-bottom: 1px solid #ebeef5;
}

.nav-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.nav-category {
  margin-bottom: 8px;
}

.nav-category-title {
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #1a73e8;
  cursor: pointer;
  transition: background 0.2s;
}

.nav-category-title:hover {
  background: #e8f0fe;
}

.nav-item {
  padding: 6px 16px 6px 24px;
  font-size: 12px;
  color: #606266;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-item:hover {
  background: #f5f7fa;
  color: #1a73e8;
}

/* 右侧内容区调整 */
.formula-page .formula-container {
  flex: 1;
  min-width: 0;
}

/* 搜索高亮效果 */
.formula-section.highlight {
  animation: highlight-pulse 2s ease-out;
}

@keyframes highlight-pulse {
  0% { box-shadow: 0 0 0 4px rgba(26, 115, 232, 0.4); }
  100% { box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08); }
}

/* 可点击的公式样式 */
.formula-item.clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.formula-item.clickable:hover {
  background: #e8f0fe;
  border-left-color: #1a73e8;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(26, 115, 232, 0.15);
}

/* 公式操作菜单 */
.formula-menu {
  position: fixed;
  z-index: 2000;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  padding: 8px 0;
  min-width: 140px;
}

.formula-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  cursor: pointer;
  font-size: 14px;
  color: #303133;
  transition: all 0.2s;
}

.formula-menu-item:hover {
  background: #f5f7fa;
  color: #1a73e8;
}

.formula-menu-item i {
  font-size: 16px;
}

/* 公式放大对话框 */
.zoom-formula-content {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 120px;
  padding: 24px;
  background: #f5f7fa;
  border-radius: 8px;
  font-size: 24px;
}

.zoom-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
}
</style>
